import * as React from 'react';
import './ui.css';
import { token, teamColor } from './theme';

/*
 * Avatar — a player seat marker (circle + initial + name). DOM/CSS replacement
 * for the SVG PlayerAvatar. Positioned by the parent Seat; this just draws the
 * marker. Ring color: gold when it's the seat's turn, team color otherwise,
 * muted when the seat is empty.
 */

interface AvatarProps {
  size: number;
  name?: string;
  teamIndex?: number;
  isActive?: boolean;
  /** This seat is taking the completed trick — marks where the pile lands. */
  isCollecting?: boolean;
  isComputer?: boolean;
  /** Seat whose player left mid-game, awaiting a replacement (paused game). */
  isVacant?: boolean;
  /** Where the name label sits relative to the circle. */
  namePlacement?: 'above' | 'below';
}

export const Avatar: React.FC<AvatarProps> = ({
  size,
  name,
  teamIndex = 0,
  isActive = false,
  isCollecting = false,
  isComputer = false,
  isVacant = false,
  namePlacement = 'below',
}) => {
  const hasPlayer = !!name;
  const initial = isVacant ? '?' : (name?.charAt(0).toUpperCase() ?? '');
  const ring =
    isVacant || !hasPlayer
      ? token('color-muted')
      : isActive
        ? token('color-gold')
        : teamColor(teamIndex);

  const label = isVacant ? (
    <div
      style={{
        marginTop: namePlacement === 'below' ? 8 : 0,
        marginBottom: namePlacement === 'above' ? 8 : 0,
        fontSize: 'var(--font-size-sm)',
        fontStyle: 'italic',
        color: token('color-muted'),
        whiteSpace: 'nowrap',
      }}
    >
      Empty seat
    </div>
  ) : hasPlayer ? (
    <div
      style={{
        marginTop: namePlacement === 'below' ? 8 : 0,
        marginBottom: namePlacement === 'above' ? 8 : 0,
        fontSize: 'var(--font-size-sm)',
        fontWeight: isActive ? 600 : 400,
        color: isActive ? token('color-text') : token('color-muted'),
        whiteSpace: 'nowrap',
      }}
    >
      {name}
      {isComputer && <span style={{ color: token('color-muted') }}> ᐧ CPU</span>}
    </div>
  ) : null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        fontFamily: token('font-sans'),
      }}
    >
      {namePlacement === 'above' && label}
      <div
        className={[
          'ui-avatar',
          isActive && !isVacant ? 'ui-avatar--active' : '',
          isCollecting ? 'ui-avatar--collecting' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          width: size,
          height: size,
          border: `3px solid ${ring}`,
          fontSize: size * 0.4,
          color: isActive && !isVacant ? token('color-gold') : token('color-avatar-initial'),
          opacity: hasPlayer && !isVacant ? 1 : 0.6,
        }}
      >
        {initial}
      </div>
      {namePlacement === 'below' && label}
    </div>
  );
};

export default Avatar;
